import { useMutation } from "@tanstack/vue-query";
import { toast } from "vue-sonner";
import { useTracking } from "@/api/composables/useTracking";
import type { TrackingEventConfig } from "@/api/config/tracking";
import type { RudderstackEventNames } from "@/types/rudderstack";

export interface NotificationConfig {
  loading?: { message: string };
  success?: { message: string };
  error?: { message: string };
}

export interface EnhancedMutationOptions<
  TData,
  TError,
  TVariables,
  TContext,
  TEventName extends RudderstackEventNames = RudderstackEventNames,
> {
  mutationFn: (variables: TVariables, signal?: AbortSignal) => Promise<TData>;
  onSuccess?: (data: TData, variables: TVariables, context: TContext | undefined) => void | Promise<void>;
  onError?: (error: TError, variables: TVariables, context: TContext | undefined) => void | Promise<void>;
  onMutate?: (variables: TVariables) => Promise<TContext | undefined> | TContext | undefined;
  onSettled?: (
    data: TData | undefined,
    error: TError | null,
    variables: TVariables,
    context: TContext | undefined,
  ) => void | Promise<void>;
  mutationKey?: string[];
  tracking?:
    | TrackingEventConfig<TEventName>
    | {
        event: (variables: TVariables) => TEventName;
        properties?: (variables: TVariables) => Record<string, any>;
      };
  notifications?: NotificationConfig;
  retry?: number;
}

export function useEnhancedMutation<
  TData,
  TError,
  TVariables,
  TContext,
  TEventName extends RudderstackEventNames = RudderstackEventNames,
>(options: EnhancedMutationOptions<TData, TError, TVariables, TContext, TEventName>) {
  const { tracking, notifications, retry, ...mutationOptions } = options;
  const { trackSuccess, trackError } = useTracking();

  let loadingToastId: string | number | undefined;
  let abortController: AbortController | undefined;

  const mutation = useMutation({
    ...mutationOptions,
    mutationFn: async (variables: TVariables) => {
      abortController = new AbortController();
      return options.mutationFn(variables, abortController.signal);
    },
    onMutate: async (variables) => {
      if (notifications?.loading) {
        loadingToastId = toast.loading(notifications.loading.message);
      }

      if (options.onMutate) {
        return await options.onMutate(variables);
      }
    },
    onSuccess: async (data, variables, context) => {
      // Track success event if configured
      if (tracking) {
        const properties = tracking.properties ? tracking.properties(variables) : {};
        const eventName = typeof tracking.event === "function" ? tracking.event(variables) : tracking.event;
        trackSuccess(eventName, properties);
      }

      // Show success toast if configured
      if (notifications?.success) {
        toast.success(notifications.success.message);
      }

      // Call original onSuccess if provided
      if (options.onSuccess) {
        await options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      // Don't show error toast or track if it was an abort
      const isAbortError = error instanceof DOMException && error.name === "AbortError";

      if (!isAbortError) {
        // Track error event if configured
        if (tracking) {
          const properties = tracking.properties ? tracking.properties(variables) : {};
          const eventName = typeof tracking.event === "function" ? tracking.event(variables) : tracking.event;
          trackError(eventName, properties);
        }

        // Show error toast if configured (empty message falls back to error.message)
        if (notifications?.error) {
          const errorMessage =
            notifications.error.message.length > 0
              ? notifications.error.message
              : error instanceof Error
                ? error.message
                : "An error occurred";
          toast.error(errorMessage);
        }
      }

      // Call original onError if provided
      if (options.onError) {
        options.onError(error as TError, variables, context);
      }
    },
    onSettled: async (data, error, variables, context) => {
      // Always dismiss loading toast when settled
      if (loadingToastId !== undefined) {
        toast.dismiss(loadingToastId);
      }

      // Call original onSettled if provided
      if (options.onSettled) {
        await options.onSettled(data, error as TError | null, variables, context);
      }
    },
    retry: retry ?? 1,
  });

  function abort() {
    abortController?.abort();
  }

  return {
    ...mutation,
    abort,
  };
}
